import type { QueryClient } from "@tanstack/react-query";
import type { Guest } from "@/types/Guest";
import { AcceptanceState } from "@/types/enums/AcceptanceState";
import { GuestState } from "@/types/enums/GuestState";
import { applyServerTruth, mergeGuestPatch } from "@/lib/utils/guest-patch";

/**
 * Keep the Requests page's CACHED row/count snapshots in step with the
 * mutations its providers apply optimistically.
 *
 * Why this exists: on `/event/:eventId/guests/requests` the seed queries live
 * in `EventGuestsRequestsPage`'s `Tables`, ABOVE the tab conditional, while
 * `RegistrantsProvider` / `CompanionsProvider` live INSIDE it
 * (`ClientWrapper.tsx`, `currentTab === … &&`). A tab switch therefore unmounts
 * the providers — losing their local rows AND their `recentlyMutatedIds`
 * shield — but never unmounts the seed observers. `invalidateQueries` with
 * `refetchType: "none"` only flips `isInvalidated`; in @tanstack/query v5 that
 * refetches when a NEW observer subscribes, and no new observer ever does here.
 * So the remount was re-seeded from the pre-mutation snapshot: a created
 * request vanished, a resolved one came back still actionable, and the tab
 * badge (held above the tabs) contradicted the table total (re-seeded from the
 * stale count query).
 *
 * Writing the mutation THROUGH to the cache fixes that at the source and,
 * unlike switching to an active refetch, has no read-after-write race: the
 * snapshot the remount reads is the one we just corrected. The `"none"`
 * invalidations stay — they still make a LATER route visit refetch real truth.
 */

type RowsKind = "companions" | "registrants";

const KINDS: readonly RowsKind[] = ["companions", "registrants"];

/** Row slot → count slot, as keyed in `EventGuestsRequestsPage`. */
const COUNT_SLOT: Record<RowsKind, string> = {
  companions: "compCount",
  registrants: "regCount",
};

/**
 * Row keys are `["guestsRequests", eventId, kind, filterJson, pageSize]` and
 * count keys `["guestsRequests", eventId, countSlot, filterJson]` — the shared
 * `filterJson` at index 3 is what lets a row removal decrement exactly the
 * count that was counting it.
 */
const FILTER_SLOT = 3;
const SIZE_SLOT = 4;

function slotQueries(qc: QueryClient, eventId: string, slot: string) {
  return qc.getQueryCache().findAll({
    predicate: (q) => {
      const key = q.queryKey as unknown[];
      return (
        key[0] === "guestsRequests" && key[1] === eventId && key[2] === slot
      );
    },
  });
}

function asRows(data: unknown): Guest[] | null {
  return Array.isArray(data) ? (data as Guest[]) : null;
}

/**
 * Ids whose OFF-PAGE count decrement has already been applied, so a repeated
 * drop cannot decrement twice. Bounded and FIFO-evicted: this is a
 * short-horizon idempotency guard for one page session, not a ledger — an
 * eviction only costs us the guard, and the on-page branch never needs it.
 */
const offPageDrops = new Set<string>();
const OFF_PAGE_DROP_MEMO_LIMIT = 500;

function claimOffPageDrop(eventId: string, guestId: string): boolean {
  const token = `${eventId}:${guestId}`;
  if (offPageDrops.has(token)) return false;
  if (offPageDrops.size >= OFF_PAGE_DROP_MEMO_LIMIT) {
    const oldest = offPageDrops.values().next().value;
    if (oldest !== undefined) offPageDrops.delete(oldest);
  }
  offPageDrops.add(token);
  return true;
}

/** Test seam — the memo above is module state and would leak across cases. */
export function __resetRequestsCacheMemo(): void {
  offPageDrops.clear();
}

/**
 * Does this freshly created guest actually belong in a REQUEST list?
 *
 * The registration rule engine can route a create straight onto the waitlist,
 * which persists `acceptanceState = SYSTEM` + `state = WAITLISTED`
 * (GuestResource.createPendingAfterValidation). Both request queries select
 * `acceptanceState = PENDING`, so a waitlisted guest is a guestlist member, not
 * a pending request — caching it would pin a request that does not exist and
 * inflate the count until a reload.
 *
 * A row with NO `acceptanceState` counts as pending on purpose: that is the
 * fallback shape used when the server did not hydrate a DTO, and being generous
 * there costs at most the pre-existing transient, whereas being strict would
 * silently reinstate the vanishing-row bug for that path.
 */
export function isPendingRequestRow(row: Guest): boolean {
  if (row.state === GuestState.WAITLISTED) return false;
  return (
    row.acceptanceState === undefined ||
    row.acceptanceState === null ||
    row.acceptanceState === AcceptanceState.PENDING
  );
}

/**
 * `filters === null` means "every cached count of this kind". A non-null set
 * restricts the adjustment to the filter variants whose ROW array actually
 * changed — see `dropGuestFromRequestsCache` for why the two differ.
 */
function adjustCounts(
  qc: QueryClient,
  eventId: string,
  kind: RowsKind,
  delta: number,
  filters: Set<string> | null,
) {
  for (const q of slotQueries(qc, eventId, COUNT_SLOT[kind])) {
    if (filters && !filters.has(String((q.queryKey as unknown[])[FILTER_SLOT])))
      continue;
    const old = qc.getQueryData(q.queryKey);
    if (typeof old !== "number") continue;
    qc.setQueryData(q.queryKey, Math.max(0, old + delta));
  }
}

/**
 * Remove a resolved request from the cached snapshots.
 *
 * `kindHint` is what the caller knows about the row: the providers pass their
 * own kind, the popup/sidebar dispatch sites pass nothing because a
 * `guestAcceptanceResolved` can be either. It only changes COUNT bookkeeping:
 * with a hint the count drops even when the row wasn't in the cached page
 * (the user was on page 2+), matching the unconditional decrement the
 * providers and `TCCAction` already do; without one, only counts whose page
 * actually lost the row are touched, so we never invent a decrement for a
 * filter the guest may not even have matched.
 */
export function dropGuestFromRequestsCache(
  qc: QueryClient,
  eventId: string,
  guestId: string,
  kindHint?: RowsKind,
): boolean {
  let rowsChanged = false;
  for (const kind of KINDS) {
    const removedFrom = new Set<string>();
    for (const q of slotQueries(qc, eventId, kind)) {
      const old = asRows(qc.getQueryData(q.queryKey));
      if (!old) continue;
      const next = old.filter((row) => row?.guestId !== guestId);
      if (next.length === old.length) continue;
      qc.setQueryData(q.queryKey, next);
      removedFrom.add(String((q.queryKey as unknown[])[FILTER_SLOT]));
      rowsChanged = true;
    }
    if (removedFrom.size > 0) {
      adjustCounts(qc, eventId, kind, -1, removedFrom);
    } else if (kind === kindHint && claimOffPageDrop(eventId, guestId)) {
      // Not on the cached page (the user was on page 2+), but we know the kind,
      // so the guest is genuinely leaving the pending set — decrement every
      // count of that kind, matching the unconditional decrement the providers
      // and TCCAction already do. `claimOffPageDrop` makes this fire AT MOST
      // ONCE per guest: without it a second call (retry, bulk loop) would
      // decrement again with nothing left to remove, silently corrupting the
      // count. The on-page branch above needs no such guard — it is naturally
      // idempotent, because the second call finds no row to remove.
      adjustCounts(qc, eventId, kind, -1, null);
    }
  }
  return rowsChanged;
}

/** Insert a just-created request at the top of the cached first page. */
export function addGuestToRequestsCache(
  qc: QueryClient,
  eventId: string,
  kind: RowsKind,
  row: Guest,
): boolean {
  if (!row.guestId) return false;
  // Symmetric with the drop path: only the counts whose page actually took the
  // row move. Bumping every variant would invent a +1 for a saved filter the
  // new guest may not match, and would double-count a re-insert of a row the
  // page already holds.
  const insertedInto = new Set<string>();
  for (const q of slotQueries(qc, eventId, kind)) {
    const old = asRows(qc.getQueryData(q.queryKey));
    if (!old) continue;
    if (old.some((existing) => existing?.guestId === row.guestId)) continue;
    const size = (q.queryKey as unknown[])[SIZE_SLOT];
    const next = [row, ...old];
    qc.setQueryData(
      q.queryKey,
      typeof size === "number" ? next.slice(0, size) : next,
    );
    insertedInto.add(String((q.queryKey as unknown[])[FILTER_SLOT]));
  }
  adjustCounts(qc, eventId, kind, +1, insertedInto);
  return insertedInto.size > 0;
}

/**
 * Actively refetch the row + count snapshots for this event.
 *
 * The counterpart to the write-through: when a COARSE reconcile fires we have
 * no patch to write, only "something changed server-side". Refreshing the
 * snapshot (rather than the provider's local rows alone) keeps the cache the
 * single truth, so the seed the next remount reads is right too. Scoped to the
 * four data slots — refetching the whole `guestsRequests` prefix would also
 * re-run the view/filter/init/role queries for nothing.
 */
export function refetchRequestsSnapshots(qc: QueryClient, eventId: string) {
  return qc.refetchQueries({
    predicate: (q) => {
      const key = q.queryKey as unknown[];
      if (key[0] !== "guestsRequests" || key[1] !== eventId) return false;
      const slot = key[2];
      return (
        slot === "companions" ||
        slot === "registrants" ||
        slot === "compCount" ||
        slot === "regCount"
      );
    },
    type: "active",
  });
}

/**
 * Write a reconciled guest (server truth pulled by the reconcile scheduler
 * after an automation wrote it) into the cached snapshots.
 *
 * Without this the cache is a STALE SHADOW of the provider's rows: the
 * providers merge `guestReconciled` into local state only, so the next cache
 * write would re-seed the grid and silently revert the automation's value. The
 * rule this module runs on is that the cache must never be behind local state.
 *
 * Deliberately {@link applyServerTruth}, not {@link mergeGuestPatch}: this IS
 * the server's row, `acceptanceState` included.
 */
export function applyServerTruthInRequestsCache(
  qc: QueryClient,
  eventId: string,
  guestId: string,
  serverGuest: Guest,
): boolean {
  let touchedAny = false;
  for (const kind of KINDS) {
    for (const q of slotQueries(qc, eventId, kind)) {
      const old = asRows(qc.getQueryData(q.queryKey));
      if (!old) continue;
      let touched = false;
      const next = old.map((row) => {
        let out = row;
        if (row?.guestId === guestId) {
          out = applyServerTruth(row, serverGuest);
          touched = true;
        }
        if (out?.mainGuestDTO?.guestId === guestId) {
          out = { ...out, mainGuestDTO: applyServerTruth(out.mainGuestDTO, serverGuest) };
          touched = true;
        }
        if (out?.companions?.some((c) => c.guestId === guestId)) {
          out = {
            ...out,
            companions: out.companions.map((c) =>
              c.guestId === guestId ? applyServerTruth(c, serverGuest) : c,
            ),
          };
          touched = true;
        }
        return out;
      });
      if (touched) {
        qc.setQueryData(q.queryKey, next);
        touchedAny = true;
      }
    }
  }
  return touchedAny;
}

/**
 * Merge a confirmed popup field-save into the cached rows.
 *
 * Mirrors what the mounted providers do to their local rows: the edited guest
 * can appear as the row itself, as a row's linked main guest, or inside a
 * row's `companions` (both feed the Verknüpfung cell).
 */
export function patchGuestInRequestsCache(
  qc: QueryClient,
  eventId: string,
  guestId: string,
  patch: Partial<Guest>,
) {
  for (const kind of KINDS) {
    for (const q of slotQueries(qc, eventId, kind)) {
      const old = asRows(qc.getQueryData(q.queryKey));
      if (!old) continue;
      let touched = false;
      const next = old.map((row) => {
        let out = row;
        if (row?.guestId === guestId) {
          out = mergeGuestPatch(row, patch);
          touched = true;
        }
        if (out?.mainGuestDTO?.guestId === guestId) {
          out = { ...out, mainGuestDTO: mergeGuestPatch(out.mainGuestDTO, patch) };
          touched = true;
        }
        if (out?.companions?.some((c) => c.guestId === guestId)) {
          out = {
            ...out,
            companions: out.companions.map((c) =>
              c.guestId === guestId ? mergeGuestPatch(c, patch) : c,
            ),
          };
          touched = true;
        }
        return out;
      });
      if (touched) qc.setQueryData(q.queryKey, next);
    }
  }
}
